import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  List,
  Modal,
  Popover,
  Row,
  Space,
  Switch,
  Table,
} from "antd";
import { formatCurrency } from "common/utils";
import currency from "currency.js";
import { useGetConsumerPortalAccountsQuery } from "features/consumerPortalDashboard/accountsAPI";
import useIsMobile from "hooks/useIsMobile";
import { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const StyledDiv = styled.div`
  margin-top: 24px;
  margin-left: 16px;
  & span {
    margin-left: 12px;
  }
  @media (max-width: 768px) {
    margin-left: 0px;
  }
`;
const StyledInfoValue = styled.div`
  margin-left: 4px;
  margin-right: 4px;
`;

const StyledRow = styled(Row)`
  flex-wrap: nowrap;
`;

const StyledSwitchLabel = styled.span`
  font-size: 12px;
`;

const StyledTable = styled(Table)`
  margin-top: 12px;
`;

const StyledList = styled(List)`
  margin-top: 20px;
  border-radius: 8px;
  overflow: hidden;
  .ant-list-item {
    background: white;
    padding: 12px;
  }
  .ant-list-footer {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const ListFooterCard = styled(Flex)`
  background: white;
  padding: 12px;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
`;

const ListTitle = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const ListSubtitle = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
`;

const ListAmount = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const ViewDetailsButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 14px;
  color: #1677ff;
  &:hover {
    color: #4096ff;
  }
  &:active {
    color: #0958d9;
  }
`;

const StyledCard = styled(Card)`
  @media (max-width: 768px) {
    background: transparent;
    border: none;
    box-shadow: none;
    .ant-card-body {
      padding: 0;
    }
  }
`;

const StyledFlex = styled(Flex)`
  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

function DashboardPage() {
  const { data: accounts = [], isLoading } = useGetConsumerPortalAccountsQuery();
  const [isZeroBalanceAccountVisible, setIsZeroBalanceAccountVisible] = useState(false);
  const isMobile = useIsMobile();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const totalBalance = accounts
    ? formatCurrency(
        accounts.reduce(
          (acc, account) => acc.add(account.totalBalance),
          currency(0, { precision: 2 }),
        ),
      )
    : "$0.00";

  const columns = [
    { title: "Creditor", dataIndex: "currentCreditorName", key: "currentCreditorName" },
    { title: "Account Number", dataIndex: "externalId", key: "externalId" },
    {
      title: "Current Balance",
      dataIndex: "totalBalance",
      key: "totalBalance",
      render: (text, record) => {
        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(record.principalBalance),
          },
          {
            // NOTE: We decided to sum up agency/client interest here as "totalInterestBalance", instead of showing them separately
            key: "interest",
            label: `Interest (${
              currency(record.interestRate, { precision: 4 }).multiply(100).value
            }%)`,
            value: formatCurrency(record.totalInterestBalance),
          },
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(record.totalFeesBalance),
          },
          {
            key: "totalAmountPaid",
            label: "Total Amount Paid",
            value: formatCurrency(record.totalAmountPaid),
          },
          // {
          //   key: "originalTotal",
          //   label: <strong>Total</strong>,
          //   value: <strong>{formatCurrency(text)}</strong>,
          // },
        ];
        const totalBalanceContent = (
          <Col>
            <Descriptions bordered column={1} size="small">
              {balanceItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );
        return (
          <StyledRow align="middle">
            <StyledInfoValue>{formatCurrency(text)}</StyledInfoValue>
            <Popover
              content={totalBalanceContent}
              title="Current Balance Breakdown"
              trigger="click"
              placement="right"
            >
              <InfoCircleOutlined style={{ flexShrink: 0 }} />
            </Popover>
          </StyledRow>
        );
      },
    },
  ];

  const onSwitchChange = (checked) => {
    setIsZeroBalanceAccountVisible(checked);
  };

  const getBalanceBreakdown = (record) => {
    const balanceItems = [
      {
        key: "principal",
        label: "Principal",
        value: formatCurrency(record.principalBalance),
      },
      {
        key: "interest",
        label: `Interest (${currency(record.interestRate, { precision: 4 }).multiply(100).value}%)`,
        value: formatCurrency(record.totalInterestBalance),
      },
      {
        key: "totalFees",
        label: "Fees",
        value: formatCurrency(record.totalFeesBalance),
      },
      {
        key: "totalAmountPaid",
        label: "Total Amount Paid",
        value: formatCurrency(record.totalAmountPaid),
      },
    ];
    return (
      <Col>
        <Descriptions bordered column={1} size="small">
          {balanceItems.map((item) => (
            <Descriptions.Item key={item.key} label={item.label}>
              {item.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Col>
    );
  };

  const filteredAccounts = !isZeroBalanceAccountVisible
    ? accounts.filter((account) => currency(account.totalBalance)?.value !== 0)
    : accounts;

  const handleViewDetails = (account) => {
    setSelectedAccount(account);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedAccount(null);
  };

  return (
    <>
      <h3>Accounts Overview</h3>
      <StyledCard>
        <StyledFlex justify="flex-end" align="end">
          <Space>
            <Switch checked={isZeroBalanceAccountVisible} onChange={onSwitchChange} />
            <StyledSwitchLabel>Show zero-balance accounts</StyledSwitchLabel>
          </Space>
        </StyledFlex>
        {isMobile ? (
          <StyledList
            loading={isLoading}
            dataSource={filteredAccounts}
            bordered={false}
            footer={
              <ListFooterCard justify="space-between">
                <strong>Current Balance</strong>
                <span>{totalBalance}</span>
              </ListFooterCard>
            }
            renderItem={(account) => {
              // Type assertion: account has same structure as items in accounts array
              const accountData = /** @type {typeof accounts[0]} */ (account);
              return (
                <List.Item key={accountData.uuid}>
                  <Flex justify="space-between" align="flex-start" flex={1}>
                    <Flex vertical>
                      <ListTitle>{accountData.currentCreditorName}</ListTitle>
                      <ListSubtitle>{accountData.externalId}</ListSubtitle>
                    </Flex>
                    <Flex vertical align="flex-end">
                      <ListAmount>{formatCurrency(accountData.totalBalance)}</ListAmount>
                      <ViewDetailsButton type="text" onClick={() => handleViewDetails(accountData)}>
                        View Details
                      </ViewDetailsButton>
                    </Flex>
                  </Flex>
                </List.Item>
              );
            }}
          />
        ) : (
          <StyledTable
            pagination={false}
            dataSource={filteredAccounts}
            columns={columns}
            loading={isLoading}
            rowKey="uuid"
          />
        )}

        <StyledDiv>
          {!isMobile && (
            <>
              <strong>Current Balance</strong>
              <span>{totalBalance}</span>
            </>
          )}

          <div style={{ fontStyle: "italic" }}>
            For questions regarding your account(s), please visit the{" "}
            <Link to="/contact">Contact Us</Link> page to find your agency's contact information.
          </div>
        </StyledDiv>
      </StyledCard>
      {isMobile && selectedAccount && (
        <Modal
          title="Current Balance Breakdown"
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={null}
        >
          {getBalanceBreakdown(selectedAccount)}
        </Modal>
      )}
    </>
  );
}

export default DashboardPage;
